import { NavLink, useParams } from "react-router";
import {
  useRltOrderShowQuery,
  useRltOrderUpdateStateMutation,
} from "@/services/backend/rlt/orders/api/order-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Order } from "@/services/backend/rlt/orders/types/order";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import { DeleteOrderDialog } from "@/routes/processes/[processId]/rlt/orders/[orderId]/_components/delete-order-dialog";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import t from "@/lang/lang";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { RltBreadcrumbs } from "@/routes/processes/[processId]/rlt/_components/rlt-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";

export function OrderRoute() {
  const { orderId } = useParams();

  const {
    data: order,
    isLoading,
    error,
  } = useRltOrderShowQuery({
    id: orderId!,
  });

  const breadcrumbs = order && (
    <RltBreadcrumbs customerId={order.customerId} processId={order.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/rlt/${order.processId!}/orders`}>
            {t("Auftragseingänge")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={order.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </RltBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {order && <Page order={order} />}
    </ProcessPageMainTemplate>
  );
}

export function Page({ order }: { order: Order }) {
  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <DocumentPage>
          <DocumentPageHeader>
            <TabNavigation />
            <DocumentPageActions>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteOrderDialog orderId={order.id} />
              <DocumentStateDropdown
                document={order}
                mutation={useRltOrderUpdateStateMutation}
              />
            </DocumentPageActions>
          </DocumentPageHeader>
          <DocumentPageContent>
            <DocumentPageNavigationOutlet />
          </DocumentPageContent>
        </DocumentPage>
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

function TabNavigation() {
  // The field names of each tab: the keys of order.ReadyForCustomer and of
  // the validation errors of the fields shown on the tab.
  const links = [
    {
      to: "data",
      label: t("Auftragseingang"),
      fieldName: ["referenceNumber", "date", "channel"],
    },
    { to: "treatment", label: t("Leistungen") },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      fieldName: [
        "serviceAddress",
        "postalAddress",
        "serviceContact",
        "representative",
      ],
    },
    {
      to: "files",
      label: t("Dateien"),
    },
  ];

  return (
    <DocumentPageNavigation>
      {links.map((link) => (
        <DocumentPageNavigationLink
          key={link.to}
          to={link.to}
          fieldName={link.fieldName}
        >
          {link.label}
        </DocumentPageNavigationLink>
      ))}
    </DocumentPageNavigation>
  );
}
